import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { plural } from '../../lib/plural';
import { FormSkeleton, LoadFailed, SectionHead, StickySave, useSaveOrLeave } from '../org-parts';
import { useOrgDraft } from '../org-draft';
import { useAdminSettings, useSaveSettings } from '../api';
import type { NotificationSettings } from '../types';

/**
 * Раздел «Уведомления»: что бот пишет клиентам и специалистам.
 *
 * Разложено по получателям, а не по видам событий: администратор приходит сюда
 * с вопросом «что увидит клиент» или «что увидит специалист», и раньше на него
 * приходилось отвечать, вычитывая приставку «Специалисту —» в середине списка.
 * Внутри группы приставка не нужна — её несёт заголовок.
 *
 * Названия правлены по тому, что на самом деле включает каждый флаг
 * (`src/modules/notifications.ts`). Ни один флаг не разбит на несколько
 * тумблеров: сервер их не различает, и два переключателя над одним значением
 * обещали бы управление, которого нет.
 */

const HOURS_BEFORE: NotificationSettings['reminders']['hoursBefore'][] = [0, 1, 2, 3, 4];
const COALESCE: NotificationSettings['coalesceMinutes'][] = [0, 5, 15, 30];

/** Строка «подпись — тумблер». Подпись переносится, тумблер сохраняет размер. */
function ToggleRow({ label, note, on, disabled, onChange }: {
  label: string;
  note?: string;
  on: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="kv-row">
      <span className="k">
        {label}
        {note ? <small>{note}</small> : null}
      </span>
      <span className="v">
        <button
          className="switch"
          type="button"
          aria-pressed={on}
          aria-label={label}
          disabled={disabled}
          onClick={() => onChange(!on)}
        />
      </span>
    </div>
  );
}

export function OrgNotifications() {
  const writable = useWritable();
  const toast = useToast();
  const data = useAdminSettings();
  const save = useSaveSettings();

  const { draft, dirty, patch, commit } = useOrgDraft<NotificationSettings>(data.data?.notifications ?? null);

  useScreen('Уведомления', null);
  useSaveOrLeave(dirty, () => submit());

  if (data.isError) {
    return <Screen><LoadFailed busy={data.isFetching} onRetry={() => void data.refetch()} /></Screen>;
  }
  if (!draft) {
    return (
      <Screen>
        <FormSkeleton rows={6} />
      </Screen>
    );
  }

  const busy = save.isPending;
  const locked = !writable || busy;

  const setReminders = (p: Partial<NotificationSettings['reminders']>) =>
    patch({ reminders: { ...draft.reminders, ...p } });
  const setClient = (p: Partial<NotificationSettings['client']>) =>
    patch({ client: { ...draft.client, ...p } });
  const setStaff = (p: Partial<NotificationSettings['staff']>) =>
    patch({ staff: { ...draft.staff, ...p } });

  /**
   * На сервер уходит только `notifications` — целиком, потому что в базе это
   * одна JSON-колонка и частичной правки у неё нет. Остальные поля карточки
   * организации запрос не упоминает и потому не трогает.
   */
  function submit(): Promise<unknown> {
    if (!draft) return Promise.resolve();
    return save.mutateAsync({ notifications: draft }).then(
      () => {
        commit(draft);
        toast('Изменения сохранены', { ms: 2000 });
      },
      (e: unknown) => {
        toast(e instanceof ApiError ? e.message : 'Не получилось сохранить');
        throw e;
      },
    );
  }

  return (
    <Screen className="orgform sticky-save">
      <p className="lead">Что бот пишет клиентам и специалистам. Отправляет бот организации.</p>

      <SectionHead title="Клиентам" />
      <div className="kv kv-wrap">
        {/* Ровно 24 часа до начала — так это считает scheduleReminders. */}
        <ToggleRow
          label="Напоминание за сутки"
          on={draft.reminders.dayBefore}
          disabled={locked}
          onChange={(v) => setReminders({ dayBefore: v })}
        />

        <label className="kv-row stack">
          <span className="k">Напоминание перед началом</span>
          <span className="v">
            <select
              aria-label="Напоминание перед началом"
              value={draft.reminders.hoursBefore}
              disabled={locked}
              onChange={(e) => setReminders({
                hoursBefore: Number(e.target.value) as NotificationSettings['reminders']['hoursBefore'],
              })}
            >
              {HOURS_BEFORE.map((h) => (
                <option key={h} value={h}>
                  {h === 0 ? 'Не отправлять' : `За ${h} ${plural(h, 'час', 'часа', 'часов')} до начала`}
                </option>
              ))}
            </select>
          </span>
        </label>

        {/* Один серверный флаг — один тумблер. `bookingConfirmed` закрывает и
            подтверждение брони, и перенос, сделанный самим клиентом: сервер их
            не различает, и разделить это на экране значило бы соврать. */}
        <ToggleRow
          label="Подтверждение записи и переноса"
          note="Когда клиент записался или сам перенёс запись"
          on={draft.client.bookingConfirmed}
          disabled={locked}
          onChange={(v) => setClient({ bookingConfirmed: v })}
        />

        <ToggleRow
          label="Запись поставлена или перенесена организацией"
          note="Изменения, сделанные специалистом или администратором"
          on={draft.client.staffChanges}
          disabled={locked}
          onChange={(v) => setClient({ staffChanges: v })}
        />

        <label className="kv-row stack">
          <span className="k">Объединять уведомления об изменениях</span>
          <span className="v">
            <select
              aria-label="Объединять уведомления об изменениях"
              value={draft.coalesceMinutes}
              disabled={locked}
              onChange={(e) => patch({
                coalesceMinutes: Number(e.target.value) as NotificationSettings['coalesceMinutes'],
              })}
            >
              {COALESCE.map((m) => (
                <option key={m} value={m}>
                  {m === 0 ? 'Не объединять — отправлять сразу' : `${m} ${plural(m, 'минута', 'минуты', 'минут')}`}
                </option>
              ))}
            </select>
          </span>
        </label>
        <p className="kv-note">
          Пока специалист раскладывает неделю, одну запись двигают по несколько раз. Сообщение
          ждёт указанное время и всё это время переписывается набело — клиент получит одно, с
          итогом. Отсчёт начинается заново после каждого изменения, поэтому сообщение уходит,
          когда правки прекратились.
        </p>

        <ToggleRow
          label="Отмены, истёкшая бронь и остановка серии"
          on={draft.client.cancellations}
          disabled={locked}
          onChange={(v) => setClient({ cancellations: v })}
        />

        <ToggleRow
          label="Смена специалиста"
          note="Когда за ребёнком закрепили другого специалиста"
          on={draft.client.assignment}
          disabled={locked}
          onChange={(v) => setClient({ assignment: v })}
        />
      </div>

      <SectionHead title="Специалистам" />
      <div className="kv kv-wrap">
        <ToggleRow
          label="Клиент записался"
          on={draft.staff.onClientBooking}
          disabled={locked}
          onChange={(v) => setStaff({ onClientBooking: v })}
        />
        <ToggleRow
          label="Клиент отменил или перенёс запись"
          on={draft.staff.onClientChanges}
          disabled={locked}
          onChange={(v) => setStaff({ onClientChanges: v })}
        />
      </div>
      <p className="hint">
        Специалист получает эти сообщения, только если у него есть вход в приложение. Просьбу
        администратора выставить расписание бот отправляет всегда — она не событие клиента.
      </p>

      {writable ? (
        <StickySave dirty={dirty} busy={busy} failed={save.isError} onClick={() => { void submit().catch(() => {}); }} />
      ) : null}
    </Screen>
  );
}
